import { IEnum } from '../services/type';
import {
  IFile,
  IPermissionTree,
  IPrepareFetchParamsOptions,
  IProfileSettingsInitialValues,
  IRolePermissions,
} from '../types';

export const getValueFromEnum = (
  enums: IEnum[],
  key: string,
): string | undefined => {
  return enums.find((obj) => obj.key === key)?.value || '';
};

export const formatISOString = (text: string) => {
  return (
    (text?.split('T')[0] ?? '') +
    ' ' +
    (text?.split('T')[1]?.split('.')[0] ?? '')
  );
};
export const formatCurrencyTRY = (amount?: number): string => {
  if (typeof amount !== 'number') {
    return '';
  }
  const result = amount.toLocaleString('tr-TR', {
    style: 'currency',
    currency: 'TRY',
  });

  return result.replace('₺', '').trim();
};

export const filterMenuItems = (items: any[]): any[] =>
  items
    .map((item) => {
      if (item && Array.isArray(item.children)) {
        const filteredChildren = filterMenuItems(item.children.filter(Boolean));
        if (filteredChildren.length === 0) return null;
        return { ...item, children: filteredChildren };
      }
      return item;
    })
    .filter(Boolean);

export const prepareFetchParameters = (
  options: IPrepareFetchParamsOptions = {},
): string => {
  const { isExpired, onlyOpen, dates, search, sort, page } = options;
  const params = new URLSearchParams();

  dates?.[0] && params.append('start_date', dates[0]);
  dates?.[1] && params.append('end_date', dates[1]);
  page?.size && params.append('limit', page.size.toString());
  typeof page?.number === 'number' &&
    params.append('offset', (page.size * page.number).toString());
  search?.length &&
    search.forEach((item) => params.append(item.key, item.value));
  sort?.key &&
    sort.order &&
    params.append(
      'ordering',
      sort.order === 'ascend' ? sort.key : `-${sort.key}`,
    );
  if (typeof isExpired === 'boolean' && isExpired) {
    params.append('expired', 'false');
    params.append('recent_status', 'issued');
  }
  if (onlyOpen) {
    params.append('only_open', 'true');
  }
  return params.size ? `?${params.toString()}` : '';
};

export const prepareFormData = <T extends Record<string, any>>(
  values: T,
): FormData => {
  const formData = new FormData();
  Object.keys(values).forEach((key) => {
    const value = values[key];
    if (key === 'upload_files') {
      if (value instanceof FileList || Array.isArray(value)) {
        Array.from(value).forEach((file: File) => {
          if (file instanceof File && file.size > 0) {
            formData.append(key, file);
          }
        });
        return;
      }
    }
    if (typeof value === 'boolean') {
      formData.append(key, value.toString());
    } else if (value !== undefined && value !== null) {
      formData.append(key, value);
    } else {
      formData.append(key, '');
    }
  });
  return formData;
};

export const prepareModalData = <
  TSource extends Record<string, any>,
  TTarget extends Record<string, any>,
>(
  item?: TSource,
): TTarget | undefined => {
  if (!item) return undefined;

  const result: any = {};

  Object.keys(item).forEach((key) => {
    if (key === 'additional_files') {
      result.upload_files = [
        new File(
          [],
          (item.additional_files ?? []).map((f: IFile) => f?.name).join(', ') ||
            '',
        ),
      ];
    } else if (key === 'gsm') {
      result[key] = item[key]?.slice(1);
    } else {
      result[key] = item[key];
    }
  });

  return result as TTarget;
};

export const cleanupAfterSubmit = <T extends object>(
  setIsModalOpen: (open: boolean) => void,
  setSelectedItem: (item?: T) => void,
  formikReset: () => void,
  handleFetchData: () => void,
) => {
  setIsModalOpen(false);
  setSelectedItem(undefined);
  formikReset();
  handleFetchData();
};

export const preparePermissionsPayload = (
  values: IProfileSettingsInitialValues,
) => {
  const { name, leftPermissions, rightPermissions, organization } = values;

  const filteredLeft = leftPermissions
    .filter((item) => !item.toString().startsWith('T-'))
    .map((item) => Number(item));
  const filteredRight = rightPermissions
    .filter((item) => !item.toString().startsWith('T-'))
    .map((item) => Number(item));

  return {
    name,
    permissions: [...filteredLeft, ...filteredRight],
    organization,
  };
};

export const extractKeys = (nodes: any[]): (string | number)[] => {
  return nodes.reduce((acc: (string | number)[], node) => {
    typeof node.key === 'number' && acc.push(node.key);
    if (node.children && Array.isArray(node.children)) {
      acc.push(...extractKeys(node.children));
    }
    return acc;
  }, []);
};

export const getFormikValuesFromRole = (
  selectedItem: IRolePermissions,
  permissionTree: IPermissionTree,
): IProfileSettingsInitialValues => {
  return {
    name: selectedItem.external_name,
    leftPermissions: selectedItem.permissions
      .filter((item) =>
        extractKeys(permissionTree?.left || []).includes(item.id),
      )
      .map((item) => item.id),
    rightPermissions: selectedItem.permissions
      .filter((item) =>
        extractKeys(permissionTree?.right || []).includes(item.id),
      )
      .map((item) => item.id),
    organization: selectedItem.organization,
  };
};
